import { Input } from '@tempo-io/sui-input';
import { useRef, useState } from 'react';
import { EmptyFieldValue } from '@/components/common/item-field-value/EmptyFieldValue';
import { FieldWithStatusIndicator } from '@/components/common/item-field-value/FieldWithStatusIndicator';
import { useCommitOnEvents } from './useCommitOnEvents';

export const TextField = ({
  id,
  name,
  value,
  isEditable = false,
  onChange,
  isPending,
  isSuccess,
  isError,
  error,
  autoFocus = false,
}: {
  id: string;
  name: string;
  value?: string;
  isEditable?: boolean;
  onChange?: (value: string) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
  autoFocus?: boolean;
}) => {
  const inputId = `text-field-${id}`;
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const { handleBlur, handleKeyDown } = useCommitOnEvents({
    value: value ?? '',
    rawValue: localValue,
    parse: (raw) => raw,
    onChange,
    inputRef,
  });

  return (
    <FieldWithStatusIndicator
      inputId={inputId}
      name={name}
      isPending={isPending}
      isSuccess={isSuccess}
    >
      {isEditable ? (
        <div className="flex flex-col gap-1">
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            disabled={isPending}
            data-testid="text-field-input"
          />
          {isError && error && (
            <span className="text-xs text-text-error" data-testid="text-field-error">
              {error.message}
            </span>
          )}
        </div>
      ) : (
        <div className="text-sm text-text-neutral-primary" data-testid="text-field-value">
          {value || <EmptyFieldValue />}
        </div>
      )}
    </FieldWithStatusIndicator>
  );
};
